import { useMemo, useState } from 'react';

import { ErrorMessage } from '../../components/ErrorMessage';
import PagesFooter from '../../components/PagesFooter/PagesFooter';
import PagesHeader from '../../components/PagesHeader/PagesHeader';
import Spinner from '../../components/Spinner/Spinner';
import { scrollToFieldWithError } from '../../helpers/scrollToFieldWithError';
import { shouldRenderQuestion } from '../../helpers/shouldRenderKycQuestion';
import { useAppDispatch, useAppSelector } from '../../hooks';
import useArnFromPath from '../../hooks/useArnFromPath';
import useComponentTracker from '../../hooks/useComponentTracker';
import { useFieldValueClearer } from '../../hooks/useFieldValueClearer';
import useGetKycService from '../../hooks/useGetKycService';
import { usePageFocus } from '../../hooks/usePageFocusHook';
import usePagesConfig from '../../hooks/usePagesConfig';
import useSaveKnowYourCustomerAnswers from '../../hooks/useSaveKycAnswersService';
import useSetBannerType from '../../hooks/useSetBannerType';
import useTranslation from '../../hooks/useTranslation';
import useValidation from '../../hooks/useValidation';
import { getApplicationState } from '../../store/application/application';
import { addOrUpdateFormField } from '../../store/applicationform';
import { selectAllKycFieldStates } from '../../store/applicationform/applicationFormSelectors';
import { WizardPageAction } from '../../store/wizard/wizard';
import { CITIZENSHIP_GROUP_ID, isSupportedQuestionType, Question } from '../../types/KycTypes';
import { convertToAnswers } from './convertToAnswers';
import KycGroup from './KycGroup';

const CoApplicantKycOngoingPage = () => {
    const translate = useTranslation();
    const dispatch = useAppDispatch();
    const { totalSections } = usePagesConfig();
    const { kycData, allQuestions, isLoading } = useGetKycService(false);
    const [pageIndex, setPageIndex] = useState<number>(0);
    const [isPatchError, setIsPatchError] = useState(false);
    const kycAnswerFieldStates = useAppSelector(selectAllKycFieldStates)();
    const saveKnowYourCustomerAnswers = useSaveKnowYourCustomerAnswers();
    const applicationReferenceNumber = useArnFromPath();
    usePageFocus(pageIndex);
    useComponentTracker('CoApplicantKycOngoingPage');
    useFieldValueClearer(kycData, kycAnswerFieldStates);

    useSetBannerType('bannerFour');

    const visibleQuestions = allQuestions
        .filter((question) => shouldRenderQuestion(question, allQuestions, kycAnswerFieldStates))
        .reduce((pages: Question[][], question) => {
            const lastPage = pages[pages.length - 1];

            if (
                lastPage &&
                ((question.groupId === CITIZENSHIP_GROUP_ID && lastPage[0].groupId === CITIZENSHIP_GROUP_ID) ||
                    (question.displayCondition?.questionId === lastPage[lastPage.length - 1].name &&
                        question.groupId === lastPage[lastPage.length - 1].groupId))
            ) {
                lastPage.push(question);
            } else {
                pages.push([question]);
            }

            return pages;
        }, []);

    const currentGroup = useMemo(
        () => kycData?.groups.find((group) => group.id === visibleQuestions[pageIndex][0].groupId),
        [kycData, pageIndex, visibleQuestions],
    );

    const pageIndexQuestions = visibleQuestions[pageIndex ?? 0]?.map((question) => question);
    const requiredFields = pageIndexQuestions?.map((field) => 'KYC_' + field.groupId + '_' + field.name) ?? [];
    const validationErrors = useValidation(requiredFields, pageIndexQuestions);
    const hasUnsupportedQuestion = !!pageIndexQuestions?.some((question) => !isSupportedQuestionType(question.type));

    const onAction = async (action: WizardPageAction) => {
        let newPageIndex = pageIndex;

        if (action === 'next') {
            if (validationErrors.length) {
                // A question whose type we have no field for is required and unanswerable, so validation would block
                // here with nothing on screen the customer can fix. Say so instead of leaving "next" dead.
                if (hasUnsupportedQuestion) {
                    setIsPatchError(true);
                }
                scrollToFieldWithError(validationErrors);
                validationErrors.forEach((validationError) => {
                    dispatch(addOrUpdateFormField(validationError));
                });
                return false;
            }

            newPageIndex++;
        } else if (action === 'back' && newPageIndex > 0) {
            newPageIndex--;
        }

        if (kycData && visibleQuestions && newPageIndex >= visibleQuestions.length) {
            const answers = convertToAnswers(kycAnswerFieldStates);

            if (!answers.length && kycData?.groups.length) {
                // Everything was answered but nothing survived the conversion, so posting would advance the
                // application on an empty answer set. Show the error rather than throwing silently.
                setIsPatchError(true);
                throw new Error('Validation errors');
            }

            try {
                setIsPatchError(false);
                await saveKnowYourCustomerAnswers(answers);
                // unwrap() so a failed refresh reaches the catch below: a createAsyncThunk resolves with a rejected
                // action instead of rejecting, which left the page sitting still with no error at all.
                await dispatch(getApplicationState({ applicationReferenceNumber })).unwrap();
            } catch (error) {
                setIsPatchError(true);
                throw error;
            }
        } else {
            setPageIndex(newPageIndex);
        }
        return true;
    };

    return (
        <>
            {isLoading || !kycData || !currentGroup ? (
                <Spinner size="xxl" />
            ) : (
                <>
                    <PagesHeader
                        actions={['back']}
                        onAction={onAction}
                        currentPage={pageIndex}
                        currentSection={4}
                        numberOfSections={totalSections}
                        numberOfPagesInSection={visibleQuestions?.length !== 0 ? visibleQuestions.length : 7}
                        currentPageInSection={pageIndex}
                        sectionTitle={translate('field.section.kyc')}
                    />
                    <KycGroup kycData={kycData} group={currentGroup} questions={visibleQuestions[pageIndex]} />
                </>
            )}
            {isPatchError && (
                <ErrorMessage title={translate('page.error.title')} description={translate('page.error.description')} />
            )}
            <PagesFooter
                actions={['next']}
                onAction={onAction}
                currentSection={4}
                numberOfSections={totalSections}
                numberOfPagesInSection={visibleQuestions?.length !== 0 ? visibleQuestions.length : 7}
                currentPageInSection={pageIndex}
                sectionTitle={translate('field.section.kyc')}
            />
        </>
    );
};

export default CoApplicantKycOngoingPage;
